import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";

import { CryptoAssetRow, useWallet } from "@/entities/wallet";
import { ROUTES } from "@/shared/config";
import { formatMoney, staggerIndex } from "@/shared/lib";
import { Panel, RoundIcon } from "@/shared/ui";
import { DepositFlow } from "@/widgets/deposit-flow";
import { DepositHologram } from "@/widgets/deposit-hologram";
import { ScreenSkeleton, ScreenState } from "@/widgets/screen-state";
import { TopBar } from "@/widgets/top-bar";

/**
 * Шаг 1 мастера пополнения.
 *
 * Список активов и сетей приходит с бэкенда в `deposit_assets` и захардкожен
 * быть не может: оператор правит реквизиты в админке. Пустая карта — законный
 * ответ (крипта не настроена), и он показывается пустым состоянием, а не
 * белым экраном.
 *
 * Схема сетей и голограмма рисуются только когда активы есть: объяснять
 * «сеть должна совпадать» на экране, где выбирать не из чего, незачем.
 *
 * Сюда же ведёт покупка робота при нехватке средств — через
 * `location.state.shortfall`. Числа берутся из ответа бэкенда как есть.
 */
export function DepositAssetPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const state = location.state as
    | { returnTo?: string; shortfall?: { available?: string; requested?: string } }
    | null;
  const shortfall = state?.shortfall;

  const { data, isPending, isError, refetch } = useWallet();

  if (isPending) return <ScreenSkeleton blocks={[80, 88, 88]} />;

  if (isError) {
    return (
      <ScreenState
        tone="loss"
        icon="warningFill"
        title={t("states.error.title")}
        body={t("states.error.body")}
        action={{ label: t("states.error.action"), onClick: () => void refetch() }}
      />
    );
  }

  const assets = Object.entries(data.deposit_assets ?? {});
  // Схема рисуется по сетям первого актива: в макете актив ровно один, и
  // рисовать по одной схеме на каждый значило бы получить простыню из
  // декораций вместо экрана выбора.
  const networks = assets[0]?.[1] ?? [];

  return (
    // `flex-1` + `mt-auto` у голограммы: в макете она стоит на дне экрана, а
    // не висит сразу под предупреждением. На экране выше 640 разница заметна —
    // иначе снизу остаётся полоса пустоты.
    <div className="flex flex-1 flex-col gap-3 px-[var(--layout-gutter)] py-3">
      <TopBar title={t("wallet.deposit")} subtitle={t("wallet.selectAsset")} />

      {shortfall?.requested ? (
        <p className="rounded-lg border border-border-loss bg-surface-loss p-4 text-small font-normal text-text-primary">
          {t("wallet.shortfall", {
            requested: formatMoney(shortfall.requested, { suffix: "USDT" }),
            available: formatMoney(shortfall.available ?? "0.00", { suffix: "USDT" }),
          })}
        </p>
      ) : null}

      {assets.length === 0 ? (
        <ScreenState
          tone="warning"
          icon="warningFill"
          title={t("wallet.noAssets.title")}
          body={t("wallet.noAssets.body")}
        />
      ) : (
        <>
          <ul className="flex flex-col gap-3">
            {assets.map(([asset, list], index) => (
              <li key={asset} className="stagger" style={staggerIndex(index)}>
                <CryptoAssetRow
                  asset={asset}
                  networks={list}
                  selected={false}
                  onSelect={() =>
                    void navigate(ROUTES.depositNetwork, {
                      state: {
                        asset,
                        networks: list,
                        ...(state?.returnTo ? { returnTo: state.returnTo } : {}),
                      },
                    })
                  }
                />
              </li>
            ))}
          </ul>

          <div className="py-4">
            <DepositFlow
              networks={networks}
              moreLabel={(count) => t("wallet.networksMore", { count })}
            />
          </div>

          <Panel as="section" radius="md" className="flex items-center gap-3 p-3">
            <RoundIcon icon="shieldCheck" size={34} tone="green" glow={false} />
            <span className="flex min-w-0 flex-col gap-1">
              <span className="text-small font-semibold text-text-primary">
                {t("wallet.networkWarningTitle")}
              </span>
              <span className="text-caption font-normal text-text-secondary">
                {t("wallet.networkWarningBody")}
              </span>
            </span>
          </Panel>

          <div className="mt-auto">
            <DepositHologram />
          </div>
        </>
      )}
    </div>
  );
}
